import { TrendingUp, CreditCard, Zap, Loader2 } from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";
import type { Objective } from "../../objectives/types";

interface AggregateMetrics {
    roiPercent: number;
    revenueUSD: number;
    peopleReached: number;
    conversions: number;
    runsCount: number;
}

interface HeroMetricsProps {
    activeObjectives: Objective[];
    aggregateMetrics: AggregateMetrics;
    loading?: boolean;
    showTutorial: boolean;
}

const HeroMetrics = ({
    activeObjectives,
    aggregateMetrics,
    loading = false,
    showTutorial,
}: HeroMetricsProps) => {
    const hasData = aggregateMetrics.runsCount > 0;
    const formatRevenue = (n: number) =>
        new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            maximumFractionDigits: 0,
        }).format(n);

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* ROI */}
            <Card
                className="p-6 relative overflow-hidden group"
                showTutorial={showTutorial}
                tutorial="Multiplicador de retorno: ingresos / costo de envíos de las campañas activas."
            >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <TrendingUp size={80} className="text-violet-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider mb-1">
                    ROI total
                </p>
                <h2 className="text-5xl font-bold text-slate-900 mb-2 tracking-tight">
                    {loading ? (
                        <Loader2 size={36} className="animate-spin text-violet-400" />
                    ) : hasData ? (
                        `${aggregateMetrics.roiPercent}%`
                    ) : (
                        <span className="text-slate-300">—</span>
                    )}
                </h2>
                <p className="text-slate-500 text-sm">
                    {hasData
                        ? `Calculado sobre ${aggregateMetrics.runsCount} envíos`
                        : "Sin datos todavía"}
                </p>
            </Card>

            {/* Revenue */}
            <Card
                className="p-6 relative overflow-hidden group"
                showTutorial={showTutorial}
                tutorial="Ingresos brutos atribuibles a las campañas activas."
            >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <CreditCard size={80} className="text-blue-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider mb-1">
                    Ingresos generados
                </p>
                <h2 className="text-5xl font-bold text-slate-900 mb-2 tracking-tight">
                    {loading ? (
                        <Loader2 size={36} className="animate-spin text-blue-400" />
                    ) : (
                        formatRevenue(aggregateMetrics.revenueUSD)
                    )}
                </h2>
                <p className="text-slate-500 text-sm">
                    {hasData
                        ? `${aggregateMetrics.conversions.toLocaleString("es-AR")} conversiones · ${aggregateMetrics.peopleReached.toLocaleString("es-AR")} personas`
                        : "Aparecerá cuando se envíe la primera campaña"}
                </p>
            </Card>

            {/* Active Campaigns */}
            <Card
                className="p-6 relative overflow-hidden group"
                showTutorial={showTutorial}
                tutorial="Campañas en estado activo. Cada una corre según su cadencia automatizada."
            >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Zap size={80} className="text-yellow-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wider mb-1">
                    Campañas activas
                </p>
                <h2 className="text-5xl font-bold text-slate-900 mb-2 tracking-tight">
                    {loading ? (
                        <Loader2 size={36} className="animate-spin text-yellow-400" />
                    ) : (
                        activeObjectives.length
                    )}
                </h2>
                <p className="text-slate-500 text-sm flex items-center gap-2">
                    {activeObjectives.length > 0 ? (
                        <>
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                            Corriendo en automático
                        </>
                    ) : (
                        "Creá una campaña para empezar"
                    )}
                </p>
            </Card>
        </div>
    );
};

export default HeroMetrics;
